import { useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Workflow,
  Code2,
  Users,
  Target,
  ArrowRight,
  Sparkles,
  Zap,
  Shield,
  Trophy,
  ChevronDown,
  ExternalLink,
  Brain
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Container from "@/components/ui/container";

const ServicesSection = () => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const handleCardExpand = useCallback((index: number) => {
    setExpandedCard(expandedCard === index ? null : index);
  }, [expandedCard]);

  const handleCTAClick = useCallback(async (serviceTitle: string) => {
    setIsLoading(serviceTitle);
    
    // Simular carregamento para demonstração
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Aqui você adicionaria a lógica real do CTA
    console.log(`CTA clicked for: ${serviceTitle}`);
    
    setIsLoading(null);
  }, []);

  const services = [
    {
      icon: Workflow,
      title: "Fluig Workflows",
      description: "Processos pré-desenvolvidos que aceleram a implementação corporativa no Fluig.",
      expandedDescription: "Nossa biblioteca de 16 soluções pré-construídas oferece implementação rápida e eficiente para os principais processos empresariais. Cada solução é desenvolvida seguindo as melhores práticas do mercado e pode ser customizada conforme suas necessidades específicas.",
      badge: "16 Soluções",
      features: [
        "Recursos Humanos",
        "Qualidade e Tecnologia da Informação"
      ],
      expandedFeatures: [
        "Gestão de Contratos com Workflow Completo",
        "Sistema de Férias e Afastamentos",
        "Controle de Despesas e Reembolsos",
        "Portal de Fornecedores Integrado",
        "Gestão de Documentos Corporativos",
        "Controle de Ponto Eletrônico"
      ],
      benefits: [
        "Redução de 70% no tempo de implementação",
        "ROI positivo em até 3 meses",
        "Suporte técnico especializado incluído"
      ],
      color: "from-fs-purple-500 to-fs-magenta-500",
      hoverColor: "hover:shadow-fs-hover hover:border-fs-purple-500/50"
    },
    {
      icon: Code2,
      title: "Desenvolvimento Customizado",
      description: "Desenvolvimento de soluções sob medida para atender necessidades específicas do seu negócio.",
      expandedDescription: "Criamos soluções totalmente personalizadas utilizando as mais modernas tecnologias e metodologias ágeis. Nossa equipe experiente garante que cada projeto seja desenvolvido com foco em performance, escalabilidade e facilidade de manutenção.",
      badge: "Sob Medida",
      features: [
        "Análise de Requisitos",
        "Desenvolvimento Custom",
        "Integração com Sistemas",
        "Suporte Especializado"
      ],
      expandedFeatures: [
        "Levantamento completo de requisitos funcionais",
        "Arquitetura escalável e moderna",
        "APIs REST e integrações complexas",
        "Testes automatizados e QA",
        "Deploy automatizado e CI/CD",
        "Documentação técnica completa"
      ],
      benefits: [
        "Solução 100% alinhada ao seu negócio",
        "Código limpo e bem documentado",
        "Suporte técnico vitalício incluído"
      ],
      color: "from-fs-cyan-500 to-fs-purple-500",
      hoverColor: "hover:shadow-fs-hover hover:border-fs-cyan-500/50"
    },
    {
      icon: Users,
      title: "Outsourcing Prime Club",
      description: "Equipe especializada dedicada para suporte contínuo e evolução da sua plataforma Fluig.",
      expandedDescription: "O Prime Club é nosso programa premium de outsourcing que oferece uma equipe dedicada de especialistas Fluig para sua empresa. Garantimos suporte contínuo, evolução da plataforma e consultoria estratégica para maximizar o valor dos seus investimentos.",
      badge: "Premium",
      features: [
        "Equipe Dedicada",
        "Suporte",
        "Manutenção Preventiva",
        "Consultoria Estratégica"
      ],
      expandedFeatures: [
        "Time dedicado exclusivo para sua empresa",
        "Suporte técnico 24x7 com SLA garantido",
        "Monitoramento proativo da plataforma",
        "Consultoria estratégica mensal",
        "Relatórios executivos periódicos",
        "Atualizações e melhorias contínuas"
      ],
      benefits: [
        "Redução de 60% nos custos de TI",
        "SLA de 99.9% de disponibilidade",
        "Foco total no core business da empresa"
      ],
      color: "from-fs-magenta-500 to-fs-cyan-500",
      hoverColor: "hover:shadow-fs-hover hover:border-fs-magenta-500/50"
    },
    {
      icon: Target,
      title: "Especialização por Área",
      description: "Consultoria especializada para diferentes departamentos e processos organizacionais.",
      expandedDescription: "Oferecemos consultoria especializada e personalizada para cada área da sua empresa. Nossos consultores possuem expertise específica em diferentes segmentos, garantindo soluções alinhadas às particularidades de cada departamento.",
      badge: "Consultoria",
      features: [
        "RH e Gestão de Pessoas",
        "Financeiro e Controladoria",
        "Jurídico e Compliance",
        "Operações e TI"
      ],
      expandedFeatures: [
        "Especialistas em processos de RH certificados",
        "Consultores financeiros com expertise TOTVS",
        "Profissionais jurídicos especializados em compliance",
        "Arquitetos de TI com foco em integração",
        "Análise de processos e mapeamento de melhorias",
        "Treinamento especializado por área"
      ],
      benefits: [
        "Consultoria especializada por segmento",
        "Metodologias comprovadas de mercado",
        "Resultados mensuráveis e auditáveis"
      ],
      color: "from-fs-purple-500 to-fs-cyan-500",
      hoverColor: "hover:shadow-fs-hover hover:border-fs-purple-500/50"
    }
  ];

  return (
    <section className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <Container className="relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-fs-purple-500/10 backdrop-blur-sm border border-fs-purple-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-fs-cyan-400" />
            <span className="text-foreground dark:text-white/70 text-sm font-medium">
              Nossas Soluções
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold font-poppins text-foreground dark:text-white mb-6">
            Escolha como
            <span className="block fs-gradient-text mt-2">
              acelerar seu Fluig
            </span>
          </h2>

          <p className="text-xl text-muted-foreground dark:text-white/70 leading-relaxed">
            4 formas de ampliar resultados sem gastar mais com licenças
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isExpanded = expandedCard === index;
            const isLoadingCTA = isLoading === service.title;
            
            return (
              <Card 
                key={service.title}
                className={`group bg-card border border-border hover:-translate-y-2 transition-all duration-500 ${service.hoverColor} animate-fade-in cursor-pointer ${
                  isExpanded ? 'lg:col-span-2 lg:row-span-1' : ''
                }`}
                style={{ animationDelay: `${index * 150}ms` }}
                onClick={() => handleCardExpand(index)}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      {/* Icon with gradient background */}
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} p-4 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-full h-full text-white" />
                      </div>

                      {/* Badge */}
                      <Badge
                        variant="secondary"
                        className="bg-fs-purple-500/20 text-fs-purple-700 dark:text-fs-purple-200 border-fs-purple-500/30 mb-3 w-fit"
                      >
                        {service.badge}
                      </Badge>
                    </div>

                    {/* Expand/Collapse Indicator */}
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-fs-purple-500/20 group-hover:bg-fs-purple-500/30 transition-colors">
                      <ChevronDown 
                        className={`w-4 h-4 text-fs-purple-300 transition-transform duration-300 ${
                          isExpanded ? 'rotate-180' : ''
                        }`} 
                      />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold font-poppins text-foreground dark:text-white mb-3 group-hover:fs-gradient-text transition-all duration-300">
                    {service.title}
                  </h3>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Description */}
                  <p className="text-muted-foreground dark:text-white/80 text-sm leading-relaxed mb-6">
                    {isExpanded && service.expandedDescription ? service.expandedDescription : service.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-2 mb-6">
                    {(isExpanded && service.expandedFeatures ? service.expandedFeatures : service.features).map((feature) => (
                      <li key={feature} className="flex items-center text-sm text-muted-foreground dark:text-white/70">
                        <Zap className="w-3 h-3 text-fs-cyan-400 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Benefits Section (only in expanded view) */}
                  {isExpanded && service.benefits && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-fs-cyan-300 mb-3">Benefícios:</h4>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit) => (
                          <li key={benefit} className="flex items-center text-sm text-muted-foreground dark:text-white/80">
                            <Shield className="w-3 h-3 text-fs-magenta-400 mr-2 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CTA Buttons */}
                  <div className={`${isExpanded ? 'flex space-x-3' : ''}`}>
                    <Button 
                      variant="fs-outline" 
                      size="sm" 
                      className={`${isExpanded ? 'flex-1' : 'w-full'} group-hover:bg-fs-purple-500/20 group-hover:border-fs-purple-500`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCTAClick(service.title);
                      }}
                      disabled={isLoadingCTA}
                    >
                      {isLoadingCTA ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          Carregando...
                        </>
                      ) : (
                        <>
                          Saiba Mais
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>

                    {isExpanded && (
                      <Button 
                        variant="fs-primary" 
                        size="sm" 
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Implementar ação de contato
                          console.log(`Contact for: ${service.title}`);
                        }}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Contratar
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-32 text-center">
          <div className="bg-gradient-to-r from-fs-purple-500/10 to-fs-cyan-500/10 backdrop-blur-sm border border-fs-purple-500/20 rounded-3xl p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <Trophy className="w-8 h-8 text-fs-cyan-400 mr-3" />
              <Shield className="w-8 h-8 text-fs-purple-400" />
            </div>
            
            <h3 className="text-2xl md:text-3xl font-bold font-poppins text-foreground dark:text-white mb-4">
              Pronto para transformar seus processos?
            </h3>

            <p className="text-muted-foreground dark:text-white/70 mb-6 max-w-2xl mx-auto">
              Venha conhecer como outras empresas reduziram até 40% do tempo gasto com aprovações com a Future Station
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Button variant="fs-primary" size="lg">
                Quero fazer meu Fluig decolar agora!
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default ServicesSection;